<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          {{ $t('sub_client.title') }}
        </h6>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">
          {{ $t('sub_client.list') }}
        </h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            :clientDropdown="clientDropdown"
            @search="getSubClients"/>

        <PerPage :values="[10, 25, 50, 100]" @update:perPage="getSubClients"/>

        <div class="row align-items-center justify-content-end mt-3">
          <button type="button" class="btn btn-outline-primary" @click="handleFormSubClient(null)">
            {{ $t('sub_client.create') }}
          </button>
        </div>

        <EmptyState
            v-if="!subClients?.data?.length"
            icon="fa-building"
            title="Keine Einträge gefunden"
            subtitle="Es wurden keine zugeordneten Jobcenter gefunden, die Ihren Suchkriterien entsprechen."/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>{{ $t('general.name') }}</th>
              <th>{{ $t('general.client') }}</th>
              <th style=" text-align: right; padding-right: 30px; min-width: 150px;">{{ $t('general.actions') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(subClient) in subClients?.data" :key="subClient.id">
              <td>{{ subClient?.name }}</td>
              <td>{{ subClient?.parent?.name }}</td>
              <td class="text-right">
                <button class="btn btn-primary"
                        @click.stop="handleFormSubClient(subClient.id!)">
                  <i class="os-icon os-icon-edit"></i>
                </button>
                <button class="btn btn-danger ml-2"
                        @click.stop="handleDeleteSubClient(subClient.id!)">
                  <i class="os-icon os-icon-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="subClients?.pagination?.total_pages ?? 0 > 1"
            :totalPages="subClients?.pagination?.total_pages"
            :currentPage="subClients?.pagination?.current_page"
            :totalItems="subClients?.pagination?.total_items"
            :perPage="subClients?.pagination?.items_per_page"
            @pagechanged="getSubClients"/>
      </div>
    </div>

    <Modal
        v-model="showModalForm"
        title="zugeordnete Jobcenter"
        :hide-cancel="true"
        :button-label="$t('general.button.save')"
        sizeModal="modal-xl"
        :hide-footer="true"
        classFooter="justify-content-center">
      <Form :subClientId="subClientId"
            :clientDropdown="clientDropdown"
            v-model:submitForm="submitForm"/>
    </Modal>
  </PageTemplate>

  <ModalDelete
      v-model="showModalDelete"
      :header="t('general.delete_item_header')"
      :onDelete="confirmDeleteSubClient">
  </ModalDelete>
</template>

<script setup lang="ts">
import clientService from '@/api/Client';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/modal/Modal.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
// @ts-ignore
import {getClientDropdown} from '@/shared/dropdowns';
import Form from '@/views/App/Client/SubClient/Form.vue';
import Search from '@/views/App/Client/SubClient/Search.vue';
import {onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {ISubClientFilters, ISubClientList} from "@/interfaces/Client/ISubClient";
import {IClientFilters} from "@/interfaces/Client/IClient";

const {t} = useI18n();
const filters = ref<ISubClientFilters>({
  name: '',
  clientId: '',
  sortBy: '',
  page: 1,
  limit: 10,
});

const subClients = ref<ISubClientList>();
const subClientId = ref<number | null>(null);
const clientDropdown = ref([]);
const isSearching = ref(false);
const showModalForm = ref(false);
const showModalDelete = ref(false);
const isLoaded = ref(false);
const submitForm = ref(false);

watch(submitForm, () => {
  showModalForm.value = false;
  showModalDelete.value = false;
  subClientId.value = null;
  getSubClients();
});

async function getSubClients(
    page: number | undefined = 1,
    perPage?: number,
    passedFilters?: IClientFilters
) {

  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = subClients.value?.pagination?.items_per_page;

  try {
    const {data} = await clientService.getSubClients({
      ...(passedFilters ? passedFilters : filters.value),
      page: page,
      limit: perPage ?? perPageRes!,
    });

    subClients.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

function handleFormSubClient(id: number | null) {
  CloseToast();
  subClientId.value = id;
  showModalDelete.value = false;
  showModalForm.value = true;
}

function handleDeleteSubClient(id: number) {
  CloseToast();
  subClientId.value = id;
  showModalDelete.value = true;
  showModalForm.value = false;
}

async function confirmDeleteSubClient() {
  try {
    await clientService.deleteSubClient(subClientId.value!);

    filters.value.clientId = '';
    filters.value.name = '';
    showModalDelete.value = false;

    await getSubClients();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  await Promise.all([
    getSubClients(),
    getClientDropdown(clientDropdown),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>

</style>